import { Entity, Example } from '../types';
import { HUMAN_MODEL } from './entityUtils';

/**
 * BQ-20: Cross-model label confusion matrix.
 *
 * Pure compute (no React). For every example, take two annotators (each may
 * be a real model name or HUMAN_MODEL='Gold'), pair their entities by partial
 * span overlap (any > 0 char overlap, BQ-13's `partial`/`type` predicate),
 * greedy left-to-right with each entity matched at most once, and tally
 * (rowLabel, colLabel) pairs. The diagonal of the resulting matrix is
 * "agreement on label"; off-diagonal cells are label disagreements with
 * spatial overlap — the candidate set for a label-normalization merge.
 *
 * Per cell we also surface a `suggestionScore`:
 *   max(cells[row][col] / rowTotals[row], cells[row][col] / colTotals[col])
 * If >= 0.8 in either direction we flag the cell as a likely merge candidate.
 * The asymmetry matters: 100% of side-A's `PER` overlapping side-B's `PERSON`,
 * but only 50% of side-B's `PERSON` overlapping side-A's `PER`, still flags
 * — the 100% direction is enough signal to suggest the merge.
 *
 * Rejected predictions (`example.rejectedPredictions`) are excluded for the
 * model side they were rejected on. Same exclusion rule as BQ-13/15. Gold
 * has no rejected-predictions surface — `humanAnnotations` are all in.
 */

export interface ConfusionCell {
  count: number;
  /** 0-1 confidence that "row→col" is a label-normalization candidate. */
  suggestionScore: number;
}

export interface ConfusionMatrix {
  rows: string[];
  cols: string[];
  cells: Record<string, Record<string, ConfusionCell>>;
  rowTotals: Record<string, number>;
  colTotals: Record<string, number>;
}

function hasOffsets(e: Entity): e is Entity & { start: number; end: number } {
  return typeof e.start === 'number' && typeof e.end === 'number';
}

function spansOverlap(
  a: { start: number; end: number },
  b: { start: number; end: number }
): boolean {
  return a.start < b.end && b.start < a.end;
}

/**
 * Resolve one annotator's contribution for an example. Returns the entity
 * list with offsets, post-rejection. For HUMAN_MODEL we read
 * `humanAnnotations`; for any other name we look up the matching
 * `modelResponses` entry and drop entities marked as rejected for that model.
 */
function entitiesFor(
  example: Example,
  side: string
): Array<Entity & { start: number; end: number }> {
  if (side === HUMAN_MODEL) {
    return (example.humanAnnotations ?? []).filter(hasOffsets);
  }
  const response = example.modelResponses.find((r) => r.modelName === side);
  if (!response) return [];

  // BQ-15 parity: skip predictions the user rejected for THIS (model, span, label).
  const rejected = new Set<string>();
  for (const r of example.rejectedPredictions ?? []) {
    if (r.modelName !== side) continue;
    const e = r.entity;
    if (!hasOffsets(e)) continue;
    rejected.add(`${e.start}-${e.end}-${e.label}`);
  }

  return response.entities
    .filter(hasOffsets)
    .filter((e) => !rejected.has(`${e.start}-${e.end}-${e.label}`));
}

/**
 * Build a confusion matrix between two annotators across all examples.
 *
 * Pairing rule: for each example, walk side-A's entities left-to-right and
 * pair each with the first unmatched side-B entity it overlaps (any > 0
 * char overlap). Each side's entities are matched at most once. For every
 * matched pair we record `(a.label, b.label)` — including diagonal (label
 * agreement) and off-diagonal (label disagreement on overlapping spans).
 *
 * Unmatched entities on either side are NOT counted: this matrix is about
 * label confusion conditioned on span agreement, not on FP/FN. BQ-13's
 * existing per-label P/R/F1 covers the unmatched side.
 *
 * Returns `rows` and `cols` as sorted arrays of every label that appears
 * on each side respectively. An off-diagonal label only present on one
 * side will still appear (with zero counts on the other axis).
 */
export function buildConfusionMatrix(
  examples: Example[],
  sideA: string,
  sideB: string
): ConfusionMatrix {
  const rowSet = new Set<string>();
  const colSet = new Set<string>();
  const cells: Record<string, Record<string, ConfusionCell>> = {};
  const rowTotals: Record<string, number> = {};
  const colTotals: Record<string, number> = {};

  const bump = (rowLabel: string, colLabel: string) => {
    rowSet.add(rowLabel);
    colSet.add(colLabel);
    if (!cells[rowLabel]) cells[rowLabel] = {};
    const existing = cells[rowLabel][colLabel];
    if (existing) {
      existing.count += 1;
    } else {
      cells[rowLabel][colLabel] = { count: 1, suggestionScore: 0 };
    }
    rowTotals[rowLabel] = (rowTotals[rowLabel] ?? 0) + 1;
    colTotals[colLabel] = (colTotals[colLabel] ?? 0) + 1;
  };

  for (const ex of examples) {
    const aEnts = entitiesFor(ex, sideA);
    const bEnts = entitiesFor(ex, sideB);
    if (aEnts.length === 0 || bEnts.length === 0) continue;

    const bMatched = new Array<boolean>(bEnts.length).fill(false);
    for (let ai = 0; ai < aEnts.length; ai++) {
      const a = aEnts[ai];
      for (let bi = 0; bi < bEnts.length; bi++) {
        if (bMatched[bi]) continue;
        const b = bEnts[bi];
        if (spansOverlap(a, b)) {
          bMatched[bi] = true;
          bump(a.label, b.label);
          break;
        }
      }
    }
  }

  // Compute suggestionScore per cell using the asymmetric max rule.
  for (const row of Object.keys(cells)) {
    for (const col of Object.keys(cells[row])) {
      const cell = cells[row][col];
      const rowTotal = rowTotals[row] ?? 0;
      const colTotal = colTotals[col] ?? 0;
      const rowConf = rowTotal > 0 ? cell.count / rowTotal : 0;
      const colConf = colTotal > 0 ? cell.count / colTotal : 0;
      cell.suggestionScore = Math.max(rowConf, colConf);
    }
  }

  return {
    rows: Array.from(rowSet).sort((a, b) => a.localeCompare(b)),
    cols: Array.from(colSet).sort((a, b) => a.localeCompare(b)),
    cells,
    rowTotals,
    colTotals,
  };
}
